import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { NavController, NavParams, ViewController } from "ionic-angular";
import { Order, OrderComponent } from "models/order-management/models";
import { OrderFormState } from "pages/orders-entry-om/add-order/order-form-state";
import { DictationTranscriptionService } from "providers/dictation-transcription/dictation-transcription.service";
import { UtilityService } from "providers/utility-service/utility-service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import {
  OrderComponentInterface,
  OrderComponentPropertyUpdate,
  OrderCreateRequestUpdate,
} from "../order-components-interface";

@Component({
  selector: "text-area",
  templateUrl: "text-area.html",
})
export class ProviderNotesComponent implements OrderComponentInterface, OnInit {
  /****************************************************************************
   * Inputs                                                                   *
   ****************************************************************************/
  @Input() orderComponent: OrderComponent;
  @Input() orderCreateRequest: Order;
  @Input() get orderFormState(): OrderFormState {
    return this._orderFormState;
  }

  /****************************************************************************
   * Outputs                                                                  *
   ****************************************************************************/
  @Output() orderCreateRequestEvent =
    new EventEmitter<OrderCreateRequestUpdate>();
  @Output() orderFormStateEvent = new EventEmitter<OrderFormState>();
  @Output() isValidEvent = new EventEmitter<boolean>();
  @Output() amendedOrderRequestEvent =
    new EventEmitter<OrderComponentPropertyUpdate>();

  /****************************************************************************
   * State                                                                    *
   ****************************************************************************/
  textFieldValue: string = "";

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public viewCtrl: ViewController,
    public stateMgmtSrvc: StateManagementService,
    public utilitySrvc: UtilityService,
    private dictationTranscriptionSrvc: DictationTranscriptionService
  ) {}
  private _orderFormState: OrderFormState;

  /****************************************************************************
   * Lifecycle Methods                                                        *
   ****************************************************************************/
  ngOnInit() {
    this.orderFormState.formType === "amendment" ||
    this.orderFormState.formType === "convertToOrder"
      ? this.setAmendedValue()
      : null;
    this.checkIsValid();
  }

  /****************************************************************************
   * Getter Methods                                                           *
   ****************************************************************************/
  public get shouldShow() {
    const componentField: string = this.orderComponent.properties[0].name;
    if (componentField === "reason") {
      return this.orderFormState.formType === "amendment";
    } else if (componentField === "providerNotes") {
      return !this.orderFormState.declined;
    } else if (componentField === "declinedReason") {
      return this.orderFormState.declined;
    } else if (componentField === "coverageDesignation") {
      // CO-3742 (REQ-006): the free-text non-coverage reason is only shown when the order is
      // NOT covered by hospice. Mirrors the dropdown component's coverageDesignation gate.
      return this.orderFormState.hospicePays === false;
    } else {
      return true;
    }
  }

  /****************************************************************************
   * Setter Methods                                                           *
   ****************************************************************************/
  setAmendedValue() {
    const componentField: string = this.orderComponent.properties[0].name;
    this.textFieldValue = this.orderCreateRequest[componentField];
    this.checkIsValid();
  }

  set orderFormState(value: OrderFormState) {
    const componentField = this.orderComponent.properties[0].name;
    const shouldClear = OrderFormState.shouldClear(
      componentField,
      this._orderFormState,
      value
    );
    this._orderFormState = value;
    if (shouldClear && !!this.textFieldValue) {
      this.textFieldValue = "";
      this.orderCreateRequestEvent.emit({
        deletes: [componentField as keyof Order],
      });
    }
    this.checkIsValid();
  }

  /****************************************************************************
   * Component Related Methods                                                *
   ****************************************************************************/
  onTextFieldValueChange(event: string) {
    let value = "";
    if (event) {
      value = event;
    }
    const componentField = this.orderComponent.properties[0].name;
    if (
      this.orderComponent.properties[0].amendable &&
      this.orderFormState.formType === "amendment"
    ) {
      const prevValue = this.orderCreateRequest[componentField];
      if (prevValue !== value) {
        const amendedObject: OrderComponentPropertyUpdate = { updates: {} };
        amendedObject.updates = {
          propertyName: componentField,
          previousValueDisplay: this.orderCreateRequest[componentField],
          previousValue: this.orderCreateRequest[componentField],
          newValue: value,
        };
        this.amendedOrderRequestEvent.emit(amendedObject);
      } else {
        const amendedObject: OrderComponentPropertyUpdate = {
          updates: {},
          deletes: [componentField as keyof Order],
        };
        this.amendedOrderRequestEvent.emit(amendedObject);
      }
    } else if (componentField === "reason") {
      const amendedObject: OrderComponentPropertyUpdate = { updates: {} };
      amendedObject.updates = {
        propertyName: componentField,
        previousValueDisplay: this.orderCreateRequest[componentField],
        previousValue: this.orderCreateRequest[componentField],
        newValue: value,
      };
      this.amendedOrderRequestEvent.emit(amendedObject);
    } else {
      const updateObject: OrderCreateRequestUpdate = { updates: {} };
      updateObject.updates[componentField] = value;
      this.orderCreateRequestEvent.emit(updateObject);
    }
    this.checkIsValid();
  }

  disableField() {
    const componentField: string = this.orderComponent.properties[0].name;
    if (
      componentField === "reason" &&
      this.orderFormState.formType === "amendment"
    ) {
      return false;
    }
    return (
      !this.orderComponent.properties[0].amendable &&
      this.orderFormState.formType === "amendment"
    );
  }

  isRequired() {
    const componentField: string = this.orderComponent.properties[0].name;
    if (componentField === "reason") {
      return this.orderFormState.formType === "amendment";
    } else if (componentField === "coverageDesignation") {
      // CO-3742 (REQ-006, AC-006-3): the free-text non-coverage reason is required ONLY when
      // Hospice Coverage = "No". When it is "Yes" the field is hidden and un-required.
      return (
        !!this.orderComponent.properties[0].required &&
        this.orderFormState.hospicePays === false
      );
    } else {
      return this.orderComponent.properties[0].required;
    }
  }

  checkIsValid() {
    // CO-3742 (REQ-006, Q3): enforce the configured character cap (500) on free-text fields.
    const max = this.orderComponent.properties[0].max;
    if (max && this.textFieldValue && this.textFieldValue.length > max) {
      this.isValidEvent.emit(false);
      return;
    }
    if (
      this.isRequired() &&
      (!this.textFieldValue || this.textFieldValue.length === 0)
    ) {
      if (this.orderFormState.declined && !!this.textFieldValue) {
        this.isValidEvent.emit(true);
      } else {
        this.isValidEvent.emit(false);
      }
    } else {
      this.isValidEvent.emit(true);
    }
  }

  /****************************************************************************
   * Cypress-Related ("data-cy") Methods                                      *
   ****************************************************************************/
  toKebabCase(label: string): string {
    if (label) {
      return label
        .replace(/^\s+|\s+$/gm, "")
        .replace(/([a-z])([A-Z])/g, "$1-$2")
        .replace(/[\s_]+/g, "-")
        .toLowerCase();
    }
    return "";
  }

  createDataCyAttribute(component: string, option: string): string {
    return `${this.toKebabCase(component)}-option-${this.toKebabCase(option)}`;
  }
}



// WEBPACK FOOTER //
// ./src/components/order-management/text-area/text-area.ts